import {
  hitTestTableCell,
  isRejectedSuperDocReceipt,
  layoutTableKey,
  listTablesForResize,
  parseTableColumnBoundaries,
  parseTableRowBoundaries,
  readSuperDocDocumentApi,
  readSuperDocTableFragments,
  resolveTableNodeIdForBlock,
  type SuperDocTableFragmentRef,
} from '@/lib/superdocTableResizeTarget';

const TABLE_FRAGMENT_SELECTOR = '.superdoc-table-fragment';
const TABLE_RESIZE_OVERLAY_SELECTOR = '[data-sd-table-resize-overlay], .v2-table-resize-overlay';

export const SUPERDOC_TABLE_COMMANDS = {
  insertRowAbove: 'table-add-row-before',
  insertRowBelow: 'table-add-row-after',
  deleteRow: 'table-delete-row',
  insertColumnLeft: 'table-add-column-before',
  insertColumnRight: 'table-add-column-after',
  deleteColumn: 'table-delete-column',
  deleteTable: 'table-delete',
} as const;

export type SuperDocTableCommandId =
  (typeof SUPERDOC_TABLE_COMMANDS)[keyof typeof SUPERDOC_TABLE_COMMANDS];

export type SuperDocTableMenuAction =
  | { type: 'command'; id: SuperDocTableCommandId }
  | { type: 'clear-cell-content' };

export interface SuperDocTableContextSnapshot {
  inTable: boolean;
  tableNodeId: string | null;
  rowIndex: number | null;
  columnIndex: number | null;
  cellNodeId: string | null;
  rows: number | null;
  columns: number | null;
}

export interface SuperDocTableMenuTarget {
  tableNodeId: string;
  rowIndex: number | null;
  columnIndex: number | null;
  cellNodeId?: string | null;
}

interface SuperDocTablesUi {
  getContext?: () => SuperDocTableContextSnapshot;
  isInTable?: () => boolean;
}

export interface SuperDocTableUiSource {
  ui?: {
    tables?: SuperDocTablesUi;
  };
  [key: string]: unknown;
}

export interface SuperDocTableMenuPoint {
  clientX: number;
  clientY: number;
  fragment: HTMLElement;
  stage?: ParentNode | null;
}

function isThenable(value: unknown): value is Promise<unknown> {
  return Boolean(value && typeof value === 'object' && typeof (value as Promise<unknown>).then === 'function');
}

function isNamespace(value: unknown): value is object {
  return Boolean(value) && (typeof value === 'object' || typeof value === 'function');
}

function readProperty(value: unknown, key: string): unknown {
  if (!isNamespace(value)) return undefined;
  try {
    return (value as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

export function isSuperDocTableCommandOk(result: unknown): boolean {
  if (result === false || result == null) return false;
  if (result === true) return true;
  return !isRejectedSuperDocReceipt(result);
}

export function tableFragmentFromPoint(
  clientX: number,
  clientY: number,
  target: EventTarget | null,
): HTMLElement | null {
  if (target instanceof Element) {
    const nested = target.closest(TABLE_FRAGMENT_SELECTOR);
    if (nested instanceof HTMLElement) return nested;
  }
  if (typeof document === 'undefined' || typeof document.elementsFromPoint !== 'function') {
    return null;
  }
  for (const node of document.elementsFromPoint(clientX, clientY)) {
    if (!(node instanceof Element)) continue;
    if (node.closest(TABLE_RESIZE_OVERLAY_SELECTOR)) continue;
    const fragment = node.closest(TABLE_FRAGMENT_SELECTOR);
    if (fragment instanceof HTMLElement) return fragment;
  }
  return null;
}

export function readSuperDocTableContext(source: unknown): SuperDocTableContextSnapshot | null {
  const ui = readProperty(source, 'ui');
  const tables = readProperty(ui, 'tables') as SuperDocTablesUi | undefined;
  if (typeof tables?.getContext !== 'function') return null;
  try {
    const snapshot = tables.getContext();
    if (!snapshot || typeof snapshot !== 'object') return null;
    return {
      inTable: Boolean(snapshot.inTable),
      tableNodeId: typeof snapshot.tableNodeId === 'string' ? snapshot.tableNodeId : null,
      rowIndex: typeof snapshot.rowIndex === 'number' ? snapshot.rowIndex : null,
      columnIndex: typeof snapshot.columnIndex === 'number' ? snapshot.columnIndex : null,
      cellNodeId: typeof snapshot.cellNodeId === 'string' ? snapshot.cellNodeId : null,
      rows: typeof snapshot.rows === 'number' ? snapshot.rows : null,
      columns: typeof snapshot.columns === 'number' ? snapshot.columns : null,
    };
  } catch {
    return null;
  }
}

export function hitTestTableCellAtPoint(
  fragment: HTMLElement,
  clientX: number,
  clientY: number,
): { rowIndex: number; columnIndex: number } | null {
  const boundaries = fragment.getAttribute('data-table-boundaries');
  const rect = fragment.getBoundingClientRect();
  return hitTestTableCell(
    parseTableColumnBoundaries(boundaries),
    parseTableRowBoundaries(boundaries),
    rect.width,
    rect.height,
    clientX - rect.left,
    clientY - rect.top,
  );
}

export function canClearTableCellContent(
  context: SuperDocTableContextSnapshot | null,
): context is SuperDocTableContextSnapshot {
  if (!context?.inTable) return false;
  return Boolean(context.cellNodeId)
    || (
      typeof context.tableNodeId === 'string'
      && typeof context.rowIndex === 'number'
      && typeof context.columnIndex === 'number'
    );
}

export function canClearTableMenuTarget(
  target: SuperDocTableMenuTarget | null,
): target is SuperDocTableMenuTarget & { rowIndex: number; columnIndex: number } {
  return Boolean(
    target
    && typeof target.tableNodeId === 'string'
    && typeof target.rowIndex === 'number'
    && typeof target.columnIndex === 'number',
  );
}

function fragmentBlockId(fragment: Element): string | null {
  return fragment.getAttribute('data-layout-block-ref') || fragment.getAttribute('data-block-id');
}

function collectMenuFragments(
  fragment: HTMLElement,
  stage?: ParentNode | null,
): SuperDocTableFragmentRef[] {
  const roots: Array<ParentNode | null | undefined> = [
    stage,
    fragment.getRootNode() instanceof Document || fragment.getRootNode() instanceof ShadowRoot
      ? fragment.getRootNode() as ParentNode
      : null,
    fragment.ownerDocument,
  ];
  const seen = new Set<string>();
  const fragments: SuperDocTableFragmentRef[] = [];
  for (const root of roots) {
    for (const candidate of readSuperDocTableFragments(root)) {
      const key = `${candidate.story}::${layoutTableKey(candidate.blockId)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      fragments.push(candidate);
    }
  }
  const blockId = fragmentBlockId(fragment);
  if (blockId) {
    const key = `${fragment.getAttribute('data-layout-story') || 'body'}::${layoutTableKey(blockId)}`;
    if (!seen.has(key)) {
      fragments.push({
        blockId,
        story: fragment.getAttribute('data-layout-story') || 'body',
      });
    }
  }
  return fragments;
}

/**
 * Resolve the right-clicked table (and cell, when hit-testable) so menu actions
 * do not depend on SuperDoc's current selection — the capture-phase menu
 * swallows the contextmenu event before SuperDoc can retarget.
 */
export async function resolveSuperDocTableMenuTarget(
  source: unknown,
  point: SuperDocTableMenuPoint,
): Promise<SuperDocTableMenuTarget | null> {
  const documentApi = readSuperDocDocumentApi(source);
  const blockId = fragmentBlockId(point.fragment);
  if (!documentApi || !blockId) return null;

  const fragments = collectMenuFragments(point.fragment, point.stage);
  const listed = await listTablesForResize(documentApi, fragments);
  const tableNodeId = resolveTableNodeIdForBlock(listed, fragments, blockId);
  if (!tableNodeId) return null;

  const cell = hitTestTableCellAtPoint(point.fragment, point.clientX, point.clientY);
  const context = readSuperDocTableContext(source);
  const contextMatchesTable = context?.tableNodeId === tableNodeId;

  return {
    tableNodeId,
    rowIndex: cell?.rowIndex ?? (contextMatchesTable ? context.rowIndex : null),
    columnIndex: cell?.columnIndex ?? (contextMatchesTable ? context.columnIndex : null),
    cellNodeId: contextMatchesTable ? context?.cellNodeId : null,
  };
}

async function invokeNamed(
  target: Record<string, unknown> | null | undefined,
  name: string,
  input: unknown,
): Promise<unknown> {
  const method = target?.[name];
  if (typeof method !== 'function') return false;
  const result = method.call(target, input);
  return isThenable(result) ? await result : result;
}

function isNoOpReceipt(result: unknown): boolean {
  if (!result || typeof result !== 'object') return false;
  return (result as { failure?: { code?: unknown } }).failure?.code === 'NO_OP';
}

function cellTextInputFromTarget(target: SuperDocTableMenuTarget): Record<string, unknown> | null {
  if (target.cellNodeId) {
    return { nodeId: target.cellNodeId, text: '' };
  }
  if (target.rowIndex != null && target.columnIndex != null) {
    return {
      nodeId: target.tableNodeId,
      rowIndex: target.rowIndex,
      columnIndex: target.columnIndex,
      text: '',
    };
  }
  return null;
}

function mutationInputForCommand(
  id: SuperDocTableCommandId,
  target: SuperDocTableMenuTarget,
): { method: string; input: Record<string, unknown> } | null {
  switch (id) {
    case SUPERDOC_TABLE_COMMANDS.insertRowAbove:
      if (target.rowIndex == null) return null;
      return {
        method: 'insertRow',
        input: { nodeId: target.tableNodeId, rowIndex: target.rowIndex, position: 'above' },
      };
    case SUPERDOC_TABLE_COMMANDS.insertRowBelow:
      if (target.rowIndex == null) return null;
      return {
        method: 'insertRow',
        input: { nodeId: target.tableNodeId, rowIndex: target.rowIndex, position: 'below' },
      };
    case SUPERDOC_TABLE_COMMANDS.deleteRow:
      if (target.rowIndex == null) return null;
      return {
        method: 'deleteRow',
        input: { nodeId: target.tableNodeId, rowIndex: target.rowIndex },
      };
    case SUPERDOC_TABLE_COMMANDS.insertColumnLeft:
      if (target.columnIndex == null) return null;
      return {
        method: 'insertColumn',
        input: { nodeId: target.tableNodeId, columnIndex: target.columnIndex, position: 'left' },
      };
    case SUPERDOC_TABLE_COMMANDS.insertColumnRight:
      if (target.columnIndex == null) return null;
      return {
        method: 'insertColumn',
        input: { nodeId: target.tableNodeId, columnIndex: target.columnIndex, position: 'right' },
      };
    case SUPERDOC_TABLE_COMMANDS.deleteColumn:
      if (target.columnIndex == null) return null;
      return {
        method: 'deleteColumn',
        input: { nodeId: target.tableNodeId, columnIndex: target.columnIndex },
      };
    case SUPERDOC_TABLE_COMMANDS.deleteTable:
      return {
        method: 'delete',
        input: { nodeId: target.tableNodeId },
      };
    default:
      return null;
  }
}

export async function executeSuperDocTableMenuAction(
  source: unknown,
  action: SuperDocTableMenuAction,
  point?: SuperDocTableMenuPoint | null,
): Promise<boolean> {
  const documentApi = readSuperDocDocumentApi(source);
  if (!documentApi) return false;

  const target = point
    ? await resolveSuperDocTableMenuTarget(source, point)
    : null;

  if (action.type === 'command') {
    if (!target) return false;
    const mutation = mutationInputForCommand(action.id, target);
    if (!mutation) return false;
    const result = await invokeNamed(documentApi.tables, mutation.method, mutation.input);
    return isSuperDocTableCommandOk(result);
  }

  if (!canClearTableMenuTarget(target)) return false;
  const input = cellTextInputFromTarget(target);
  if (!input) return false;

  const result = await invokeNamed(documentApi.tables, 'setCellText', input);
  return isNoOpReceipt(result) || isSuperDocTableCommandOk(result);
}
